import { compact } from 'lodash-es'
import { getImageDownloadFileExt } from 'src/components/ArtworkDetail/utils'
import type {
  ArtworkBaseFragment,
  Media,
  TaskSummary,
} from 'src/services/apollo/generated/graphql'
import { fetchGetMedia } from 'src/services/apollo/media'
import dayjs from './dayjs'
import { getMediaIdsFromTaskSummaries } from './task'
import { UnFile } from './unFile'
import { Zip } from './zip'

// TODO: There are too many `downloadXXX` functions.
//       We need to merge them into one.
export function downloadFile(src: string | Blob, filename: string) {
  const directSrc = typeof src === 'string'
  const a = document.createElement('a')
  a.href = directSrc ? src : URL.createObjectURL(src)
  a.download = filename
  a.click()
  if (!directSrc) {
    URL.revokeObjectURL(a.href)
  }
}

export type DownloadState = { progress: number; total: number }
type OnProgress = (state: DownloadState) => void

export async function downloadFiles(
  files: UnFile[],
  filename: string,
  onProgress?: OnProgress,
) {
  const total = files.length
  const zip = new Zip()
  for (const [idx, file] of files.entries()) {
    const actualFile = await file.toFile()
    // name must be read after toFile() — it may be corrected by the response Content-Type
    zip.file(file.name, actualFile)
    onProgress?.({ progress: idx + 1, total })
  }
  // We only provide compressed image formats (like png, webp).
  // So an extra compression is not necessary.
  // It also saves some performance.
  downloadFile(await zip.generateAsync({ level: 0 }), filename)
}

export async function downloadArtworks(
  artworks: Pick<ArtworkBaseFragment, 'id' | 'media'>[],
  onProgress?: OnProgress,
) {
  const files = compact(
    artworks.map(({ id, media }) => {
      if (media) {
        return UnFile.fromMedia(
          media,
          `pixai-artwork_${id}.${getImageDownloadFileExt({ media })}`,
        )
      }
    }),
  )
  await downloadFiles(
    files,
    `pixai-artworks_${dayjs().format('YYYY-MM-DD-HH-mm-ss')}.zip`,
    onProgress,
  )
}

export async function downloadTaskSummaryMedias(
  tasks: TaskSummary[],
  onProgress?: (state: DownloadState) => void,
) {
  const mediaIds = getMediaIdsFromTaskSummaries(tasks)
  const medias = new Map<string, Media | undefined>()
  for (const id of mediaIds) {
    const media = await fetchGetMedia({ id }).then(res => res.data?.media)
    medias.set(id, media)
  }
  const files: UnFile[] = []
  for (const task of tasks) {
    const mediaIds = getMediaIdsFromTaskSummaries([task])
    for (const [idx, mediaId] of mediaIds.entries()) {
      const media = medias.get(mediaId)
      if (media) {
        files.push(
          UnFile.fromMedia(
            media,
            `pixai-task_${task.id}${
              mediaIds.length > 1 ? `_${idx + 1}` : ''
            }.${getImageDownloadFileExt({ media })}`,
          ),
        )
      }
    }
  }
  await downloadFiles(
    files,
    `pixai-tasks_${dayjs().format('YYYY-MM-DD-HH-mm-ss')}.zip`,
    onProgress,
  )
}
